import {
  DataGridPro,
  getGridStringOperators,
  GridColDef,
  GridFilterInputValueProps,
  GridFilterModel,
  GridPaginationModel,
  GridRowModel,
  GridSortItem,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import combinedQuery from 'graphql-combine-query'
import { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import {
  getApplicationTypesQuery,
  getFundingRoundsByProcess,
} from 'src/screens/administration/round-management/round-management-queries'
import { fetchPfKapRows, FetchRowsPromise } from 'src/screens/pf-kap/index/PfKapGridUtils'
import { fetchPfPgvRows } from 'src/screens/pf-pgv/index/PfPgvGridUtils'
import {
  fetchMilestoneConfigsForProcessAndType,
  getMilestoneTitle,
} from 'src/screens/shared/implementation/details/milestone/utils/MilestoneUtils'
import { fetchGfchResponsibleUsersForProcessQuery } from 'src/screens/shared/project/index/projectGridQueries'
import { KAP_MODULES, PF_KAP_MODULES, PROJECT, PROJECT_STATUSES, PROJECT_TYPE } from 'src/shared/constants/constants'
import { MILESTONE_TYPE } from 'src/shared/constants/milestone-constants'
import { Option } from 'src/shared/form/control'
import { AutoCompleteFilterInput } from 'src/shared/form/grid/filter/AutoCompleteFilterInput'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import {
  autoCompleteContainsArrayFilterOperator,
  getGridStringsOperatorsIgnoreAccents,
} from 'src/shared/grid/DataGridFilters'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { useLocalStorage } from 'src/shared/utils/hooks/local-storage-hooks'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { STORAGE_KEY } from 'src/shared/utils/StorageUtils'
import { Utils } from 'src/shared/utils/Utils'
import { UserContext, useUserLocale } from 'src/user/UserContext'
import styled from 'styled-components'
import { useClient } from 'urql'

const DataGridProStyled = styled(DataGridPro)`
  & .MuiDataGrid-row {
    &:hover {
      cursor: pointer;
    }
  }
`

interface Props {
  process: PROJECT_TYPE
  ProjectToolbar: ReactElement
}

const PAGE_SIZE = 10

type ProjectCockpitTableSettings = {
  sort?: GridSortItem
  filtering?: GridFilterModel
  rowsPerPage?: number
}

export const ProjectCockpitGrid = ({ process, ProjectToolbar }: Props): ReactElement => {
  const { getMessage } = useMessageSource()
  const notificationService = useNotificationService()
  const gridTranslations = useGridTranslateHook()
  const gridRef = useGridApiRef()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const locale = useUserLocale()

  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<GridRowModel[]>([])
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [applicationTypeOptions, setApplicationTypeOptions] = useState<Option[]>([])
  const [fundingRoundOptions, setFundingRoundOptions] = useState<Option[]>([])
  const [gfchResponsibleOptions, setGfchResponsibleOptions] = useState<Option[]>([])

  const storageKey = process === PROJECT.PF_KAP ? STORAGE_KEY._PF_KAP_STORAGE_V2 : STORAGE_KEY._PF_PGV_STORAGE_V2

  const [tableSettings, setTableSettings] = useLocalStorage<ProjectCockpitTableSettings>(storageKey, {
    rowsPerPage: PAGE_SIZE,
  })

  const sortModel: GridSortModel = useMemo(() => {
    return tableSettings?.sort ? [tableSettings?.sort] : []
  }, [tableSettings?.sort])

  const paginationModel: GridPaginationModel = useMemo(
    () => ({
      page: currentPage,
      pageSize: tableSettings?.rowsPerPage ?? PAGE_SIZE,
    }),
    [tableSettings?.rowsPerPage, currentPage],
  )

  const { user } = useContext(UserContext)
  const globalRoles = user.roles
  const isGfsUser = Utils.isInternalRole(globalRoles)

  const modules = useMemo(
    () => (process === 'PF_KAP' || process === 'PF_PGV' ? PF_KAP_MODULES : KAP_MODULES),
    [process],
  )

  const handleRowClick = (params: GridRowModel) => {
    const projectId = params?.id
    if (process === PROJECT.PF_KAP) {
      navigate(ROUTES.PfKapDetailsRoot.nested.BasicInformation.params({ projectId }))
    } else if (process === PROJECT.PF_PGV) {
      navigate(ROUTES.PfPgvDetailsRoot.nested.BasicInformation.params({ projectId }))
    } else {
      throw Error(`Unknown process ${process}`)
    }
  }

  const autoCompleteEqualsFilterOperator = (options: Option[], label: string, placeholder: string) =>
    getGridStringOperators()
      .filter((operator) => operator.value === 'equals')
      .map((operator) => ({
        ...operator,
        InputComponent: (gridFilterProps: GridFilterInputValueProps) => (
          <AutoCompleteFilterInput {...gridFilterProps} options={options} label={label} placeholder={placeholder} />
        ),
      }))

  const milestoneTypeOptions = useMemo(
    () =>
      Object.values(MILESTONE_TYPE).map((milestoneType) => {
        return { label: getMessage(`label.milestone.type.${milestoneType}`), value: milestoneType }
      }),
    [getMessage],
  )

  const columns = useMemo<GridColDef[]>(() => {
    const columnsAndFilters: GridColDef[] = [
      {
        field: 'id',
        flex: 0.5,
        headerName: getMessage('label.project.id'),
        filterOperators: getGridStringOperators().filter((x) => x.value === 'equals'),
        renderCell: ({ row }) => `${getMessage(`label.project.id.character.${process.toLowerCase()}`)}${row.id}`,
      },
      {
        field: 'short_title',
        flex: 2,
        headerName: getMessage('label.short.title'),
        filterOperators: getGridStringsOperatorsIgnoreAccents().filter((x) => x.value === 'contains'),
      },
      {
        field: 'status',
        flex: 0.5,
        headerName: getMessage('label.project.status'),
        filterOperators: autoCompleteEqualsFilterOperator(
          PROJECT_STATUSES.map((x) => ({
            label: getMessage(`label.project.status.${x}`),
            value: x,
          })),
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
        valueGetter: (value: any) => {
          return value?.value ?? ''
        },
        renderCell: (params) => {
          return params.row.status.label ?? ''
        },
      },
      {
        field: 'funding_round_name',
        flex: 0.8,
        headerName: getMessage('label.funding.round'),
        filterOperators: autoCompleteEqualsFilterOperator(
          fundingRoundOptions,
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
      },
      {
        field: 'application_type',
        flex: 1,
        headerName: getMessage('label.application_type'),
        filterOperators: autoCompleteEqualsFilterOperator(
          applicationTypeOptions,
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
        valueGetter: (value: any) => {
          return value?.value ?? ''
        },
        renderCell: (params) => {
          return params.row.application_type.label ?? ''
        },
      },
      {
        field: 'next_milestone',
        flex: 1,
        headerName: getMessage('label.next.milestone'),
        sortable: false,
        filterOperators: autoCompleteEqualsFilterOperator(
          milestoneTypeOptions,
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
        valueGetter: (value: any) => {
          return value?.value ?? ''
        },
        renderCell: (params) => {
          return params.row.next_milestone.label ?? ''
        },
      },
      {
        field: 'gfch_responsible',
        flex: 1.5,
        headerName: getMessage('label.gfch.responsible'),
        filterOperators: autoCompleteEqualsFilterOperator(
          gfchResponsibleOptions,
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
      },
    ]

    if (process === PROJECT.PF_KAP) {
      columnsAndFilters.push({
        field: 'modules',
        flex: 0.5,
        headerName: getMessage('label.modules'),
        filterOperators: autoCompleteContainsArrayFilterOperator(
          modules.map((x) => ({
            label: getMessage(`label.module.${x}`),
            value: x,
          })),
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
          // NOTE: KBL 2025-05-21 the label is kept for backwards compatible
          getMessage('label.grid.equals'),
        ),
        sortComparator: (v1, v2) => {
          const a = Array.isArray(v1) ? v1.join(', ') : ''
          const b = Array.isArray(v2) ? v2.join(', ') : ''
          return a.localeCompare(b)
        },
        valueGetter: (value: any) => {
          return value.map((item: any) => item.value) ?? []
        },
        renderCell: (params) => {
          return params.row.modules?.map((item: any) => item.label)?.join(', ') ?? ''
        },
      })
    }

    return columnsAndFilters
  }, [
    getMessage,
    fundingRoundOptions,
    applicationTypeOptions,
    milestoneTypeOptions,
    gfchResponsibleOptions,
    process,
    modules,
  ])

  const fetchRowsByProcess = useCallback(async (): Promise<FetchRowsPromise> => {
    if (process === PROJECT.PF_KAP) {
      return fetchPfKapRows(urqlClient)(isGfsUser)
    } else if (process === PROJECT.PF_PGV) {
      return fetchPfPgvRows(urqlClient)(isGfsUser)
    } else {
      throw Error(`Unknown process: ${process}`)
    }
  }, [process, urqlClient, isGfsUser])

  const fetchRows = useCallback(async () => {
    setLoading(true)
    try {
      const { rows } = await fetchRowsByProcess()
      const { data } = await fetchMilestoneConfigsForProcessAndType(process, undefined, urqlClient)

      const rowsAdapted = rows.map((r) => {
        const noNextMilestoneText = getMessage('label.no.available.milestone')
        const nextMilestoneConfig =
          data?.milestone_config?.filter((i) => i.process === process && i.type === r.curr_milestone_type)?.[0] ??
          undefined
        const nextMilestoneText = r.curr_milestone_type
          ? `${getMilestoneTitle(nextMilestoneConfig, locale)} ${r.curr_milestone_year ?? ''}`
          : noNextMilestoneText

        return {
          ...r,
          status: {
            label: getMessage(`label.project.status.${r.status}`),
            value: r.status,
          },
          modules: r.modules?.map((m) => ({ value: m, label: getMessage(`label.module.${m}`) })) ?? [],
          application_type: {
            label: getMessage(r.application_type),
            value: r.application_type,
          },
          next_milestone: {
            value: r.curr_milestone_type,
            label: nextMilestoneText,
          },
        }
      })

      setRows(rowsAdapted)
    } catch (e) {
      notificationService.operationFailed()
    } finally {
      setLoading(false)
    }
  }, [process, notificationService, getMessage, fetchRowsByProcess])

  const onGridPaginationChange = useCallback(
    (currentPaginationModel: GridPaginationModel) => {
      const currentPage = currentPaginationModel.page
      const currentRowsPerPage = currentPaginationModel.pageSize

      setTableSettings({ ...tableSettings, rowsPerPage: currentRowsPerPage })
      setCurrentPage(currentPage)
    },
    [setTableSettings, tableSettings],
  )

  const onGridFilterModelChange = useCallback(
    (currentGridFilterModel: GridFilterModel) => {
      setTableSettings({ ...tableSettings, filtering: currentGridFilterModel })
    },
    [setTableSettings, tableSettings],
  )

  const onGridSortItemChange = useCallback(
    (currentGridSortItem: GridSortItem) => {
      setTableSettings({ ...tableSettings, sort: currentGridSortItem })
    },
    [setTableSettings, tableSettings],
  )

  useEffect(() => {
    const initData = async () => {
      const { document, variables } = combinedQuery('applicationTypesAndFundingRoundsAndGfchResponsibleUsers')
        .add(getApplicationTypesQuery, { userProcesses: [process] })
        .add(getFundingRoundsByProcess, { process })
        .add(fetchGfchResponsibleUsersForProcessQuery, { dossierType: process })

      const { data } = await urqlClient
        .query<{
          application_type: Query_Root['application_type']
          funding_round: Query_Root['funding_round']
          project_base: Query_Root['project_base']
        }>(document, variables)
        .toPromise()

      if (data && data?.application_type && data?.funding_round) {
        setApplicationTypeOptions(
          data?.application_type.map((type) => ({
            label: getMessage(type.key),
            value: type.key,
          })),
        )
        setFundingRoundOptions(
          data?.funding_round.map((round) => ({
            label: `${round.name}`,
            value: `${round.name}`,
          })),
        )
        if (data?.project_base.length > 0) {
          setGfchResponsibleOptions(
            data?.project_base.map((project) => ({
              label: `${project.gfch_responsible?.first_name} ${project.gfch_responsible?.last_name}`,
              value: `${project.gfch_responsible?.first_name} ${project.gfch_responsible?.last_name}`,
            })),
          )
        }
      } else {
        notificationService.operationFailed()
      }
    }

    initData()
  }, [urqlClient, getMessage, notificationService, process])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  return (
    <>
      <DataGridContainer>
        <DataGridProStyled
          paginationModel={paginationModel}
          filterModel={tableSettings?.filtering}
          sortModel={sortModel}
          rows={rows}
          localeText={gridTranslations}
          columns={columns}
          disableRowSelectionOnClick={true}
          apiRef={gridRef as any}
          onPaginationModelChange={(currentPaginationModel) => onGridPaginationChange(currentPaginationModel)}
          onSortModelChange={(currentSortModel) => onGridSortItemChange(currentSortModel[0])}
          onFilterModelChange={(currentFilterModel) => onGridFilterModelChange(currentFilterModel)}
          onRowClick={(params) => handleRowClick(params)}
          pagination
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableColumnPinning
          columnVisibilityModel={{
            modules: process !== PROJECT.PF_PGV,
            next_milestone: isGfsUser,
          }}
          slots={{
            toolbar: () => ProjectToolbar,
          }}
        />
      </DataGridContainer>
    </>
  )
}
